/**
 * AP-28 9.2 — das Artikeldetail: **ein Blatt mit Abschnitten**, keine Reiter.
 *
 * Dieselbe Entscheidung wie beim Geschäftspartner (AP-31 8.2): Ein Abschnitt,
 * den der Benutzer nicht sieht, kann nichts senden.
 *
 * Die Rollenschalter stehen im Kopf der Stammdaten und wirken sofort: Wer die
 * Einkaufsrolle setzt, sieht den Einkaufsabschnitt.
 *
 * Seit **AP-36 Phase E speichert die Seite feldweise ohne Knopf** — Text, Zahl
 * und Datum beim Verlassen des Felds, Auswahl und Kontrollkästchen sofort; die
 * drei Unterlisten sind Zeilenlisten, deren leere letzte Zeile anlegt.
 *
 * Nachtrag (13.9.2026): Gewicht je Einheit (N-7), Abschnitt Einheiten (N-5),
 * Kundenpreise im Verkauf (N-8), freie Preiseinheit (N-12), Hinweis nach dem
 * Verkleinern eines Bildes (N-13).
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAssistentDatensatz } from '@/lib/assistent/assistent.store'
import { useTranslation } from 'react-i18next'
import { Star, Trash2 } from 'lucide-react'
import { CURRENCIES, erfuelltRecht, istGueltigeEan, normalisiereEan } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { bestaetige } from '@/lib/dialoge'
import { KlappUmschalter, klappTexte, useKlappModus } from '@/components/klappmodus'
import {
  Feld as Spalte,
  RasterInfo,
  ZeilenBlock,
  ZeilenListe,
  ZusatzFeld,
  zelle,
  type Spalte as SpaltenDef,
} from '@/components/zeilenblock'
import {
  FeldStatusZeichen,
  FeldZeile,
  Feldgruppe,
  feldKlasse,
  useAenderung,
  useEntwurf,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type FeldOptionen,
  type Gruppe,
  type Speicherstelle,
} from '@/lib/speichern'
import { articlesApi } from '../api/articles.api'
import { ArtAbzeichen, RollenKuerzel } from '../components/ArticleBadges'
import {
  Abschnittskarte,
  GewichtRahmen,
  PreiseinheitEingabe,
  gewichtKlasse,
  zahlKlasse,
} from '../components/ArticleFormParts'
import { lesePreiseinheit } from '../components/PriceUnitInput'
import { alsEingabe, formatMegabyte, formatPreis, leseDezimal, zuVieleStellen } from '../lib/article-format'
import type {
  AltUnitPayload,
  ArticleAltUnit,
  ArticleDetail,
  ArticleGroup,
  ArticlePayload,
  ArticleSupplierRow,
  ArticleUnit,
  CustomerPrice,
  CustomerRef,
  SupplierRef,
} from '../types/article.types'
import { ARTICLE_KINDS, MAX_ALT_UNITS } from '../types/article.types'

type Uebersetzer = (key: string, options?: Record<string, unknown>) => string

export default function ArticleDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuthStore()

  const darf = useCallback((p: string) => erfuelltRecht(p, user?.permissions), [user?.permissions])
  const canManage = darf('articles.article.manage')
  const canDelete = darf('articles.article.delete')
  const sichtEinkauf = darf('articles.purchase.view')
  const darfPartnerLesen = darf('crm.client.read')

  /**
   * Der geladene Datensatz. Er reicht den Stand an die Speicherstelle weiter und
   * trägt die Zeilen der beiden Unterlisten mit eigenem Stand (Kundenpreise,
   * Lieferanten): Anlegen und Entfernen ändern den Artikel nicht, ein Neuladen
   * käme dort also nicht als neuer Stand an.
   */
  const [geladen, setGeladen] = useState<ArticleDetail | null>(null)
  // AP-18: Der Assistent weiß, welcher Artikel geöffnet ist.
  useAssistentDatensatz(geladen ? { art: 'article', id: geladen.id, bezeichnung: t('assistant.record.article', { number: geladen.articleNumber, name: geladen.name }) } : null)
  const [einheiten, setEinheiten] = useState<ArticleUnit[]>([])
  const [gruppen, setGruppen] = useState<ArticleGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setErrorText] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)

  /** Eine Fehlermeldung verdrängt einen alten Erfolgshinweis. */
  const setError = useCallback((text: string | null) => {
    if (text) setHinweis(null)
    setErrorText(text)
  }, [])

  // AP-36: eine Stelle je Artikel. Die Alternativeinheiten ersetzen die ganze
  // Liste über ihren eigenen Endpunkt (8.3 a) — mit dem Stand des Artikels, den
  // der Server in derselben Transaktion anfasst.
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `article:${id}`,
    bezeichnung: geladen ? `${geladen.articleNumber} · ${geladen.name}` : t('articles.title'),
    daten: geladen as unknown as Datensatz | null,
    schreibe: (patch, stand) =>
      ('altUnits' in patch
        ? articlesApi.replaceAltUnits(id, patch.altUnits as AltUnitPayload[], stand)
        : articlesApi.update(id, patch as ArticlePayload, stand)
      ).then((r) => r.data as unknown as Datensatz),
    keepalive: { url: `/articles/${id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const stand = useStellenDaten(stelle)

  const laden = useCallback(async () => {
    if (!id) return
    try {
      const { data } = await articlesApi.get(id)
      setGeladen(data)
      setError(null)
    } catch (err) {
      setError(fehlertext(err, t))
    } finally {
      setLoading(false)
    }
  }, [id, t, setError])

  useEffect(() => {
    void laden()
  }, [laden])

  useEffect(() => {
    articlesApi.units().then(({ data }) => setEinheiten(data.data)).catch(() => undefined)
    articlesApi.groups().then(({ data }) => setGruppen(data.data)).catch(() => undefined)
  }, [])

  if (loading) return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>
  if (!geladen) return <div className="p-6 text-sm text-destructive">{error ?? t('articles.notFound')}</div>

  // Bis die Stelle den geladenen Stand übernommen hat (Layout-Effekt), zeigt die
  // Seite den geladenen Datensatz — sonst stünde sie für einen Zug leer da.
  const artikel = (stand.id ? (stand as unknown as ArticleDetail) : geladen)

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <Kopf
        artikel={artikel}
        canManage={canManage}
        canDelete={canDelete}
        onZurueck={() => navigate('/articles')}
        onAktiv={async () => {
          try {
            setGeladen((await articlesApi.setActive(artikel.id, !artikel.isActive)).data)
          } catch (err) {
            setError(fehlertext(err, t))
          }
        }}
        onGeloescht={() => navigate('/articles')}
        onFehler={setError}
      />

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {hinweis && (
        <div className="rounded-md border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-foreground">{hinweis}</div>
      )}

      <div className="max-w-5xl space-y-6">
        <StammdatenAbschnitt
          stelle={stelle}
          artikel={artikel}
          einheiten={einheiten}
          gruppen={gruppen}
          canManage={canManage}
        />

        <EinheitenAbschnitt stelle={stelle} artikel={artikel} einheiten={einheiten} canManage={canManage} />

        {artikel.isSalesArticle && (
          <VerkaufAbschnitt
            stelle={stelle}
            artikel={artikel}
            zeilen={geladen.customerPrices ?? []}
            canManage={canManage}
            darfPartnerLesen={darfPartnerLesen}
            onGeaendert={laden}
            onFehler={setError}
          />
        )}

        {artikel.isPurchaseArticle &&
          (sichtEinkauf ? (
            <EinkaufAbschnitt
              artikel={artikel}
              zeilen={geladen.suppliers ?? []}
              canManage={canManage}
              darfPartnerLesen={darfPartnerLesen}
              onGeaendert={laden}
              onFehler={setError}
            />
          ) : (
            <GesperrterAbschnitt titel={t('articles.sections.purchase')} />
          ))}

        <BildAbschnitt
          artikel={artikel}
          canManage={canManage}
          onGeaendert={setGeladen}
          onHinweis={setHinweis}
          onFehler={setError}
        />

        {(artikel.externalId || artikel.externalSource) && <FremdsystemAbschnitt artikel={artikel} />}
      </div>
    </div>
  )
}

// ─── Kopf ─────────────────────────────────────────────────────────────────────

function Kopf({
  artikel,
  canManage,
  canDelete,
  onZurueck,
  onAktiv,
  onGeloescht,
  onFehler,
}: {
  artikel: ArticleDetail
  canManage: boolean
  canDelete: boolean
  onZurueck: () => void
  onAktiv: () => void
  onGeloescht: () => void
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  const [bestaetigen, setBestaetigen] = useState(false)

  const loeschen = async () => {
    try {
      await articlesApi.purge(artikel.id)
      onGeloescht()
    } catch (err) {
      onFehler(fehlertext(err, t))
      setBestaetigen(false)
    }
  }

  return (
    <div className="space-y-3">
      <button type="button" onClick={onZurueck} className="text-sm text-muted-foreground hover:text-foreground">
        ‹ {t('articles.title')}
      </button>

      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{artikel.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <span className="font-mono">{artikel.articleNumber}</span>
            <ArtAbzeichen kind={artikel.kind} />
            <RollenKuerzel sales={artikel.isSalesArticle} purchase={artikel.isPurchaseArticle} />
            {!artikel.isActive && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{t('articles.status.inactive')}</span>
            )}
          </div>
        </div>

        {canManage && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onAktiv}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
            >
              {artikel.isActive ? t('articles.actions.deactivate') : t('articles.actions.activate')}
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={() => setBestaetigen(true)}
                className="rounded-md border border-destructive/40 px-3 py-1.5 text-sm text-destructive hover:bg-destructive/10"
              >
                {t('articles.actions.delete')}
              </button>
            )}
          </div>
        )}
      </div>

      {bestaetigen && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
          <p className="text-sm text-foreground">{t('articles.actions.deleteConfirm', { number: artikel.articleNumber })}</p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setBestaetigen(false)}
              className="rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void loeschen()}
              className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive/90"
            >
              {t('articles.actions.delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Bausteine ────────────────────────────────────────────────────────────────
//
// Abschnittskarte und die Feldklassen stehen in `ArticleFormParts` — die
// Anlageseite (`ArticleNewPage`) nutzt dieselben.

/** Ein gesperrter Abschnitt ist ein Hinweis, **dass es ihn gibt** — kein Loch. */
function GesperrterAbschnitt({ titel }: { titel: string }) {
  const { t } = useTranslation()
  return (
    <section className="rounded-lg border border-dashed border-border px-4 py-3">
      <h2 className="font-semibold text-muted-foreground">🔒 {titel}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{t('articles.sections.locked')}</p>
    </section>
  )
}

/** Menge größer 0 mit höchstens drei Nachkommastellen (wie `Decimal(12,3)`). */
const gueltigeMenge = (n: number | null): n is number => n !== null && !Number.isNaN(n) && n > 0 && !zuVieleStellen(n, 3)

/** 0.0042 → „0,0042" — ohne die Nullen, die `toFixed` anhängen würde. */
const zahlAlsEingabe = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n).replace('.', ','))

/** Ein Betrag: nie negativ, höchstens zwei Nachkommastellen (dafür gibt es die Preiseinheit). */
function betragFeld(t: Uebersetzer, label: string, gesperrt: boolean, gruppe?: Gruppe): FeldOptionen<number | null> {
  return {
    gruppe,
    gesperrt,
    label,
    art: 'zahl',
    // `alsEingabe` statt der Vorgabe: ein Preis steht mit beiden Stellen da.
    schreib: (w) => alsEingabe(w, 2),
    pruefe: (w) =>
      w === null ? null : w < 0 ? t('articles.priceInvalid') : zuVieleStellen(w, 2) ? t('articles.priceDecimals') : null,
  }
}

/** Die Preiseinheit: eine ganze Zahl von 1 bis 1.000.000, nie leer (N-12). */
function preiseinheitFeld(t: Uebersetzer, gesperrt: boolean, gruppe?: Gruppe): FeldOptionen<number | null> {
  return {
    gruppe,
    gesperrt,
    label: t('articles.fields.priceUnit'),
    pflicht: true,
    lies: (x) => (x.trim() === '' ? null : (lesePreiseinheit(x) ?? Number.NaN)),
    schreib: (w) => (w === null || w === undefined || Number.isNaN(w) ? '' : String(w)),
    pruefe: (w) => (w === null || Number.isNaN(w) ? t('articles.priceUnitInvalid') : null),
  }
}

// ─── Stammdaten ───────────────────────────────────────────────────────────────

function StammdatenAbschnitt({
  stelle,
  artikel,
  einheiten,
  gruppen,
  canManage,
}: {
  stelle: Speicherstelle<unknown>
  artikel: ArticleDetail
  einheiten: ArticleUnit[]
  gruppen: ArticleGroup[]
  canManage: boolean
}) {
  const { t } = useTranslation()
  const gesperrt = !canManage

  const nummer = useFeld(stelle, 'articleNumber', { label: t('articles.fields.articleNumber'), pflicht: true, gesperrt })
  const ean = useFeld<string | null>(stelle, 'ean', {
    label: t('articles.fields.ean'),
    gesperrt,
    lies: (x) => normalisiereEan(x) || null,
    // N-19: dieselbe Prüfziffern-Regel wie am Server — ein Zahlendreher fällt schon hier auf.
    pruefe: (w) => (w && !istGueltigeEan(w) ? t('articles.eanInvalid') : null),
  })
  const name = useFeld(stelle, 'name', { label: t('articles.fields.name'), pflicht: true, gesperrt })
  const art = useFeld(stelle, 'kind', { art: 'auswahl', label: t('articles.fields.kind'), pflicht: true, gesperrt })
  const gruppe = useFeld<string | null>(stelle, 'groupId', { art: 'auswahl', label: t('articles.fields.group'), gesperrt })
  const einheit = useFeld(stelle, 'unitId', { art: 'auswahl', label: t('articles.fields.unit'), pflicht: true, gesperrt })
  const gewicht = useFeld<number | null>(stelle, 'weightPerUnit', {
    art: 'zahl',
    // Kilogramm je Einheit, vier Nachkommastellen (N-7); leer = nicht gepflegt.
    stellen: 4,
    label: t('articles.fields.weightPerUnit'),
    gesperrt,
    pruefe: (w) => (w !== null && (w < 0 || zuVieleStellen(w, 4)) ? t('articles.weightInvalid') : null),
  })
  const belegtext = useFeld<string | null>(stelle, 'documentText', { label: t('articles.fields.documentText'), gesperrt })
  const notiz = useFeld<string | null>(stelle, 'internalNote', { label: t('articles.fields.internalNote'), gesperrt })

  // Die eigene Einheit bleibt wählbar, auch wenn sie inzwischen deaktiviert ist.
  const waehlbar = einheiten.filter((u) => u.isActive || u.id === artikel.unitId)
  const gruppenWaehlbar = gruppen.filter((g) => g.isActive || g.id === artikel.groupId)
  const einheitKuerzel = einheiten.find((u) => u.id === einheit.wert)?.code ?? artikel.unit.code

  return (
    <Abschnittskarte
      titel={t('articles.sections.base')}
      aktion={
        // Verkauf und Einkauf prüft der Server **zusammen** (mindestens eine
        // Rolle) — deshalb eine Gruppe, die beide Werte in einem PATCH schickt.
        <Feldgruppe
          stelle={stelle}
          label={t('articles.fields.roles')}
          pruefe={(werte) => (werte.isSalesArticle || werte.isPurchaseArticle ? null : t('articles.roleRequired'))}
        >
          <Rollen stelle={stelle} gesperrt={gesperrt} />
        </Feldgruppe>
      }
    >
      <div className="space-y-2">
        <FeldZeile label={t('articles.fields.articleNumber')} status={nummer}>
          <input maxLength={40} className={`${feldKlasse} font-mono`} {...nummer.props} />
        </FeldZeile>
        <FeldZeile label={t('articles.fields.ean')} status={ean}>
          <input inputMode="numeric" maxLength={20} className={`${feldKlasse} font-mono tabular-nums`} {...ean.props} />
        </FeldZeile>
        <FeldZeile label={t('articles.fields.name')} status={name}>
          <input maxLength={200} className={`${feldKlasse} text-lg font-semibold`} {...name.props} />
        </FeldZeile>

        <FeldZeile label={t('articles.fields.kind')} status={art}>
          <select className={feldKlasse} {...art.props}>
            {ARTICLE_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`articles.kinds.${k}`)}
              </option>
            ))}
          </select>
        </FeldZeile>
        <FeldZeile label={t('articles.fields.group')} status={gruppe}>
          <select className={feldKlasse} {...gruppe.props}>
            <option value="">—</option>
            {gruppenWaehlbar.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </FeldZeile>
        <FeldZeile label={t('articles.fields.unit')} status={einheit}>
          <select className={feldKlasse} {...einheit.props}>
            {waehlbar.length === 0 && <option value={artikel.unitId}>{artikel.unit.code}</option>}
            {waehlbar.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code} — {u.name} ({t('articles.lists.decimalsShort', { count: u.decimals })})
              </option>
            ))}
          </select>
        </FeldZeile>

        <FeldZeile label={t('articles.fields.weightPerUnit')} status={gewicht} hilfe={t('articles.hints.weight')}>
          <GewichtRahmen einheit={einheitKuerzel}>
            <input inputMode="decimal" className={gewichtKlasse} {...gewicht.props} />
          </GewichtRahmen>
        </FeldZeile>

        <FeldZeile label={t('articles.fields.documentText')} status={belegtext} hilfe={t('articles.hints.documentText')}>
          <textarea rows={3} maxLength={4000} className={feldKlasse} {...belegtext.props} />
        </FeldZeile>
        <FeldZeile label={t('articles.fields.internalNote')} status={notiz} hilfe={t('articles.hints.internalNote')}>
          <textarea rows={2} maxLength={4000} className={feldKlasse} {...notiz.props} />
        </FeldZeile>
      </div>
    </Abschnittskarte>
  )
}

/** Die beiden Rollenschalter — Kontrollkästchen speichern sofort (AP-36 3.2). */
function Rollen({ stelle, gesperrt }: { stelle: Speicherstelle<unknown>; gesperrt: boolean }) {
  const { t } = useTranslation()
  const verkauf = useFeld<boolean>(stelle, 'isSalesArticle', { art: 'schalter', label: t('articles.role.sales'), gesperrt })
  const einkauf = useFeld<boolean>(stelle, 'isPurchaseArticle', { art: 'schalter', label: t('articles.role.purchase'), gesperrt })
  return (
    <div className="flex items-center gap-4 text-sm">
      <label className="flex items-center gap-2">
        <input type="checkbox" {...verkauf.schalter} />
        {t('articles.role.sales')}
      </label>
      <label className="flex items-center gap-2">
        <input type="checkbox" {...einkauf.schalter} />
        {t('articles.role.purchase')}
      </label>
    </div>
  )
}

// ─── Einheiten ────────────────────────────────────────────────────────────────

interface AltEntwurf {
  unitId: string
  quantity: string
  baseQuantity: string
}

const LEERE_ALT: AltEntwurf = { unitId: '', quantity: '', baseQuantity: '' }

const altListe = (aktuell: Datensatz): ArticleAltUnit[] => (aktuell.altUnits as ArticleAltUnit[] | undefined) ?? []

const altNutzlast = (z: ArticleAltUnit): AltUnitPayload => ({
  unitId: z.unitId,
  quantity: z.quantity,
  baseQuantity: z.baseQuantity,
})

const altEntwurfAus = (z: ArticleAltUnit): AltEntwurf => ({
  unitId: z.unitId,
  quantity: zahlAlsEingabe(z.quantity),
  baseQuantity: zahlAlsEingabe(z.baseQuantity),
})

/** Grund, warum eine Zeile nicht hinausgeht — oder `null`. */
function pruefeAlt(e: AltEntwurf, t: Uebersetzer): string | null {
  if (!e.unitId) return t('articles.altUnits.incomplete')
  if (!gueltigeMenge(leseDezimal(e.quantity)) || !gueltigeMenge(leseDezimal(e.baseQuantity))) {
    return t('articles.altUnitQuantityInvalid')
  }
  return null
}

const altNutzlastAus = (e: AltEntwurf): AltUnitPayload => ({
  unitId: e.unitId,
  quantity: leseDezimal(e.quantity) as number,
  baseQuantity: leseDezimal(e.baseQuantity) as number,
})

/**
 * Alternativeinheiten (N-5) — gespeichert wird das Verhältnis **wie eingegeben**
 * („10 Stangen = 60 lfm"), und immer die ganze Liste auf einmal (`PUT`).
 *
 * Deshalb hat keine Zeile eine eigene Speicherstelle: Jede Änderung ist eine
 * **Operation auf der Artikel-Stelle** (AP-36 8.3 a), die bei einem Konflikt auf
 * dem neuen Stand noch einmal angewendet wird.
 */
function EinheitenAbschnitt({
  stelle,
  artikel,
  einheiten,
  canManage,
}: {
  stelle: Speicherstelle<unknown>
  artikel: ArticleDetail
  einheiten: ArticleUnit[]
  canManage: boolean
}) {
  const { t } = useTranslation()
  const zeilen = altListe(useStellenDaten(stelle))
  const basis = artikel.unit.code

  /** Nie die Artikeleinheit selbst, keine Einheit doppelt; eine gewählte inaktive bleibt stehen. */
  const waehlbarFuer = (eigene: string) =>
    einheiten.filter(
      (u) =>
        u.id !== artikel.unitId &&
        (u.isActive || u.id === eigene) &&
        !zeilen.some((z) => z.unitId !== eigene && z.unitId === u.id),
    )
  const nochFrei = einheiten.some((u) => u.isActive && u.id !== artikel.unitId && !zeilen.some((z) => z.unitId === u.id))
  const voll = zeilen.length >= MAX_ALT_UNITS

  const spalten: SpaltenDef[] = [
    { key: 'menge', label: t('articles.altUnits.quantity'), spur: '6rem', min: 70, schmal: 'w-24', rechts: true },
    { key: 'einheit', label: t('articles.altUnits.altUnit'), spur: 'minmax(10rem,2fr)', min: 150, schmal: 'w-52' },
    { key: 'gleich', label: '', spur: '1.5rem', min: 20, schmal: 'w-6' },
    { key: 'basis', label: t('articles.altUnits.baseQuantity', { unit: basis }), spur: 'minmax(8rem,1fr)', min: 110, schmal: 'w-28' },
    { key: 'aktion', label: '', spur: '4rem', min: 45, schmal: 'w-16' },
  ]

  return (
    <Abschnittskarte titel={t('articles.sections.units')}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">{t('articles.altUnits.hint')}</p>

        <ZeilenListe spalten={spalten}>
          {zeilen.length === 0 && (
            <p className="px-4 py-3 text-sm text-muted-foreground">{t('articles.altUnits.none')}</p>
          )}
          {zeilen.map((z) => (
            <AltZeile
              key={z.id}
              stelle={stelle}
              zeile={z}
              basis={basis}
              einheiten={waehlbarFuer(z.unitId)}
              canManage={canManage}
            />
          ))}
          {canManage && !voll && nochFrei && (
            <NeueAltZeile stelle={stelle} basis={basis} einheiten={waehlbarFuer('')} />
          )}
        </ZeilenListe>

        <p className="text-xs text-muted-foreground">
          {voll
            ? t('articles.altUnits.max', { max: MAX_ALT_UNITS })
            : !nochFrei && einheiten.length > 0
              ? t('articles.altUnits.noUnits')
              : ''}
        </p>
      </div>
    </Abschnittskarte>
  )
}

function AltZeile({
  stelle,
  zeile,
  basis,
  einheiten,
  canManage,
}: {
  stelle: Speicherstelle<unknown>
  zeile: ArticleAltUnit
  basis: string
  einheiten: ArticleUnit[]
  canManage: boolean
}) {
  const { t } = useTranslation()
  const eintrag = useEntwurf<AltEntwurf>(stelle, {
    kennung: `altUnits:${zeile.id}`,
    gespeichert: altEntwurfAus(zeile),
    felder: ['altUnits'],
    label: t('articles.sections.units'),
    gesperrt: !canManage,
    pruefe: (e) => pruefeAlt(e, t),
    operation: (e) => (aktuell) => ({
      altUnits: altListe(aktuell).map((z) => (z.id === zeile.id ? altNutzlastAus(e) : altNutzlast(z))),
    }),
  })
  const entfernen = useAenderung(stelle, `altUnits:${zeile.id}:weg`)
  const e = eintrag.entwurf ?? altEntwurfAus(zeile)
  const setze = (teil: Partial<AltEntwurf>) => eintrag.setze((alt) => ({ ...alt, ...teil }))

  return (
    <ZeilenBlock
      schluessel={zeile.id}
      onVerlassen={() => void eintrag.speichere()}
      onVerwerfen={eintrag.verwerfen}
      fehler={eintrag.fehler}
    >
      <Spalte spalte="menge">
        <input
          inputMode="decimal"
          aria-label={t('articles.altUnits.quantity')}
          placeholder="1"
          disabled={!canManage}
          className={`${zelle} text-right tabular-nums`}
          data-start
          value={e.quantity}
          onChange={(ev) => setze({ quantity: ev.target.value })}
        />
      </Spalte>
      <Spalte spalte="einheit">
        <select
          aria-label={t('articles.altUnits.altUnit')}
          disabled={!canManage}
          className={zelle}
          value={e.unitId}
          onChange={(ev) => setze({ unitId: ev.target.value })}
        >
          <option value="">{t('articles.altUnits.choose')}</option>
          {e.unitId && !einheiten.some((u) => u.id === e.unitId) && (
            <option value={e.unitId}>
              {zeile.unit.code} — {zeile.unit.name}
            </option>
          )}
          {einheiten.map((u) => (
            <option key={u.id} value={u.id}>
              {u.code} — {u.name}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="gleich" label="">
        <span className="block text-center text-base text-muted-foreground">=</span>
      </Spalte>
      <Spalte spalte="basis">
        <span className="flex items-center gap-1">
          <input
            inputMode="decimal"
            aria-label={t('articles.altUnits.baseQuantity', { unit: basis })}
            placeholder="2"
            disabled={!canManage}
            className={`${zelle} text-right tabular-nums`}
            value={e.baseQuantity}
            onChange={(ev) => setze({ baseQuantity: ev.target.value })}
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">{basis}</span>
        </span>
      </Spalte>
      <Spalte spalte="aktion">
        <span className="flex items-center gap-1">
          <FeldStatusZeichen status={eintrag} />
          {canManage && (
            <button
              type="button"
              className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              title={t('articles.actions.remove')}
              aria-label={t('articles.actions.remove')}
              onClick={() =>
                void entfernen.sende({
                  felder: ['altUnits'],
                  operation: (aktuell) => ({
                    altUnits: altListe(aktuell)
                      .filter((z) => z.id !== zeile.id)
                      .map(altNutzlast),
                  }),
                })
              }
            >
              <Trash2 size={14} />
            </button>
          )}
          <FeldStatusZeichen status={entfernen.status} />
        </span>
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: hängt die Einheit beim Verlassen an die Liste an. */
function NeueAltZeile({
  stelle,
  basis,
  einheiten,
}: {
  stelle: Speicherstelle<unknown>
  basis: string
  einheiten: ArticleUnit[]
}) {
  const { t } = useTranslation()
  const [neu, setNeu] = useState<AltEntwurf>(LEERE_ALT)
  const [fehler, setFehler] = useState<string | null>(null)
  const anlegen = useAenderung(stelle, 'altUnits:neu')

  const anhaengen = async () => {
    if (!neu.unitId && !neu.quantity && !neu.baseQuantity) return
    const grund = pruefeAlt(neu, t)
    if (grund) {
      setFehler(grund)
      return
    }
    setFehler(null)
    const ergebnis = await anlegen.sende({
      felder: ['altUnits'],
      operation: (aktuell) => ({ altUnits: [...altListe(aktuell).map(altNutzlast), altNutzlastAus(neu)] }),
    })
    if (ergebnis.art === 'gespeichert') setNeu(LEERE_ALT)
  }

  return (
    <ZeilenBlock
      neu
      onVerlassen={() => void anhaengen()}
      onVerwerfen={() => {
        setNeu(LEERE_ALT)
        setFehler(null)
      }}
      fehler={fehler ?? anlegen.status.fehler}
    >
      <Spalte spalte="menge">
        <input
          inputMode="decimal"
          aria-label={t('articles.altUnits.quantity')}
          placeholder="1"
          className={`${zelle} text-right tabular-nums`}
          value={neu.quantity}
          onChange={(ev) => setNeu((n) => ({ ...n, quantity: ev.target.value }))}
        />
      </Spalte>
      <Spalte spalte="einheit">
        <select
          aria-label={t('articles.altUnits.altUnit')}
          className={zelle}
          value={neu.unitId}
          onChange={(ev) => setNeu((n) => ({ ...n, unitId: ev.target.value }))}
        >
          <option value="">{t('articles.altUnits.choose')}</option>
          {einheiten.map((u) => (
            <option key={u.id} value={u.id}>
              {u.code} — {u.name}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="gleich" label="">
        <span className="block text-center text-base text-muted-foreground">=</span>
      </Spalte>
      <Spalte spalte="basis">
        <span className="flex items-center gap-1">
          <input
            inputMode="decimal"
            aria-label={t('articles.altUnits.baseQuantity', { unit: basis })}
            placeholder="2"
            className={`${zelle} text-right tabular-nums`}
            value={neu.baseQuantity}
            onChange={(ev) => setNeu((n) => ({ ...n, baseQuantity: ev.target.value }))}
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">{basis}</span>
        </span>
      </Spalte>
      <Spalte spalte="aktion" leer>
        <FeldStatusZeichen status={anlegen.status} />
      </Spalte>
    </ZeilenBlock>
  )
}

// ─── Verkauf ──────────────────────────────────────────────────────────────────

function VerkaufAbschnitt({
  stelle,
  artikel,
  zeilen,
  canManage,
  darfPartnerLesen,
  onGeaendert,
  onFehler,
}: {
  stelle: Speicherstelle<unknown>
  artikel: ArticleDetail
  zeilen: CustomerPrice[]
  canManage: boolean
  darfPartnerLesen: boolean
  onGeaendert: () => Promise<void>
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  const gesperrt = !canManage

  const preis = useFeld<number | null>(stelle, 'salesPrice', betragFeld(t, t('articles.fields.salesPriceLong'), gesperrt))
  const einheit = useFeld<number | null>(stelle, 'priceUnit', preiseinheitFeld(t, gesperrt))
  const satz = useFeld<number | null>(stelle, 'taxRate', {
    art: 'zahl',
    label: t('articles.fields.taxRateLong'),
    gesperrt,
    pruefe: (w) => (w !== null && (w < 0 || w > 100 || zuVieleStellen(w, 2)) ? t('articles.taxRateInvalid') : null),
  })

  const vorschau =
    preis.wert !== null && einheit.wert !== null && !Number.isNaN(einheit.wert)
      ? formatPreis(preis.wert, einheit.wert, artikel.unit.code)
      : '—'

  return (
    <Abschnittskarte titel={t('articles.sections.sales')}>
      <div className="space-y-2">
        <FeldZeile label={t('articles.fields.salesPriceLong')} status={preis} hilfe={t('articles.hints.salesPrice')}>
          <input inputMode="decimal" className={zahlKlasse} {...preis.props} />
        </FeldZeile>
        <FeldZeile label={t('articles.fields.priceUnit')} status={einheit} hilfe={`→ ${vorschau}`}>
          <PreiseinheitEingabe className={zahlKlasse} {...einheit.props} />
        </FeldZeile>
        <FeldZeile label={t('articles.fields.taxRateLong')} status={satz} hilfe={t('articles.hints.taxRate')}>
          <input inputMode="decimal" className={zahlKlasse} {...satz.props} />
        </FeldZeile>
        <p className="text-xs text-muted-foreground">{t('articles.hints.priceUnit')}</p>
      </div>

      <KundenpreiseTeil
        artikel={artikel}
        zeilen={zeilen}
        canManage={canManage}
        darfPartnerLesen={darfPartnerLesen}
        onGeaendert={onGeaendert}
        onFehler={onFehler}
      />
    </Abschnittskarte>
  )
}

// ─── Kundenpreise (Teil des Verkaufs) ─────────────────────────────────────────

/** Der heutige Tag als `JJJJ-MM-TT` — in **Ortszeit**, denn „heute" meint den Tag des Benutzers. */
function heuteLokal(): string {
  const d = new Date()
  const zwei = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`
}

/** „Gültig bis" darf nicht vor „Gültig ab" liegen — ISO-Tage vergleichen sich als Text richtig. */
function pruefeZeitraum(werte: Datensatz, t: Uebersetzer): string | null {
  const ab = werte.validFrom as string | null
  const bis = werte.validTo as string | null
  return ab && bis && bis < ab ? t('articles.customerPriceValidRange') : null
}

/**
 * N-8/N-18 — ein fester Preis je Kunde **für einen Zeitraum**. Im Beleg gewinnt
 * er, wenn er am Belegdatum gilt (N-10); nach „Gültig bis" gilt wieder der
 * Verkaufspreis. Ein Kunde kann mehrere Preise nacheinander haben, aber keine
 * zwei, die sich überschneiden — das prüft der Server am zusammengeführten
 * Zeitraum, weshalb „Gültig ab" und „Gültig bis" zusammen gespeichert werden.
 */
function KundenpreiseTeil({
  artikel,
  zeilen,
  canManage,
  darfPartnerLesen,
  onGeaendert,
  onFehler,
}: {
  artikel: ArticleDetail
  zeilen: CustomerPrice[]
  canManage: boolean
  darfPartnerLesen: boolean
  onGeaendert: () => Promise<void>
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  // Auftraggeber 10.10.2026: Zeilen immer aufgeklappt, immer zugeklappt oder beim Bearbeiten — je Benutzer gemerkt.
  const [klappModus, setKlappModus] = useKlappModus('article_customer_prices')
  const [kunden, setKunden] = useState<CustomerRef[]>([])
  const [kundenFehler, setKundenFehler] = useState(false)

  useEffect(() => {
    if (!canManage || !darfPartnerLesen) return
    articlesApi
      .customers()
      .then(({ data }) => setKunden(data.data))
      .catch(() => setKundenFehler(true))
  }, [canManage, darfPartnerLesen])

  const spalten: SpaltenDef[] = [
    { key: 'kunde', label: t('articles.customerPrices.customer'), spur: 'minmax(10rem,2fr)', min: 150, schmal: 'w-full' },
    { key: 'preis', label: t('articles.customerPrices.price'), spur: '7rem', min: 85, schmal: 'w-28', rechts: true },
    { key: 'einheit', label: t('articles.customerPrices.priceUnit'), spur: '6rem', min: 70, schmal: 'w-24', rechts: true },
    { key: 'ab', label: t('articles.customerPrices.validFrom'), spur: '9.5rem', min: 130, schmal: 'w-40' },
    { key: 'bis', label: t('articles.customerPrices.validTo'), spur: '9.5rem', min: 130, schmal: 'w-40' },
    { key: 'aktion', label: '', spur: '4rem', min: 45, schmal: 'w-16' },
  ]

  return (
    <div className="mt-6 border-t border-border pt-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold text-foreground">{t('articles.customerPrices.title')}</h3>
        <KlappUmschalter modus={klappModus} onModus={setKlappModus} texte={klappTexte(t)} />
      </div>
      <p className="mb-3 text-xs text-muted-foreground">{t('articles.customerPrices.hint')}</p>

      <ZeilenListe spalten={spalten} klappModus={klappModus}>
        {zeilen.length === 0 && (
          <p className="px-4 py-3 text-sm text-muted-foreground">{t('articles.customerPrices.none')}</p>
        )}
        {zeilen.map((z) => (
          <KundenpreisZeile
            key={z.id}
            artikelId={artikel.id}
            einheitKuerzel={artikel.unit.code}
            zeile={z}
            canManage={canManage}
            onGeaendert={onGeaendert}
            onFehler={onFehler}
          />
        ))}
        {canManage &&
          (darfPartnerLesen && !kundenFehler ? (
            <NeuerKundenpreis artikelId={artikel.id} kunden={kunden} onAngelegt={onGeaendert} />
          ) : (
            <p className="px-4 py-3 text-sm text-destructive">{t('articles.customerPrices.loadFailed')}</p>
          ))}
      </ZeilenListe>
    </div>
  )
}

function KundenpreisZeile({
  artikelId,
  einheitKuerzel,
  zeile,
  canManage,
  onGeaendert,
  onFehler,
}: {
  artikelId: string
  einheitKuerzel: string
  zeile: CustomerPrice
  canManage: boolean
  onGeaendert: () => Promise<void>
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  const gesperrt = !canManage
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `articleCustomerPrice:${zeile.id}`,
    bezeichnung: zeile.customer.name,
    daten: zeile as unknown as Datensatz,
    schreibe: (patch, stand) =>
      articlesApi
        .updateCustomerPrice(artikelId, zeile.id, patch as Parameters<typeof articlesApi.updateCustomerPrice>[2], stand)
        .then((r) => r.data as unknown as Datensatz),
    keepalive: { url: `/articles/${artikelId}/customer-prices/${zeile.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  // Die Zeile ist eine Gruppe: „Gültig ab" und „Gültig bis" gehen zusammen
  // hinaus, weil der Server den zusammengeführten Zeitraum prüft (N-18).
  const gruppe = useGruppe(stelle, {
    label: zeile.customer.name,
    pruefe: (werte) => pruefeZeitraum(werte, t),
  })
  const daten = useStellenDaten(stelle)

  const preis = useFeld<number | null>(stelle, 'price', {
    ...betragFeld(t, t('articles.customerPrices.price'), gesperrt, gruppe),
    pflicht: true,
  })
  const einheit = useFeld<number | null>(stelle, 'priceUnit', preiseinheitFeld(t, gesperrt, gruppe))
  const ab = useFeld<string | null>(stelle, 'validFrom', {
    gruppe,
    gesperrt,
    art: 'datum',
    pflicht: true,
    label: t('articles.customerPrices.validFrom'),
  })
  const bis = useFeld<string | null>(stelle, 'validTo', {
    gruppe,
    gesperrt,
    art: 'datum',
    label: t('articles.customerPrices.validTo'),
  })
  const notiz = useFeld<string | null>(stelle, 'note', { gruppe, gesperrt, label: t('articles.customerPrices.note') })

  // Abgelaufen: im Beleg gilt wieder der Verkaufspreis. Künftig: gilt noch nicht.
  const heute = heuteLokal()
  const gueltigBis = (daten.validTo as string | null) ?? null
  const gueltigAb = (daten.validFrom as string | null) ?? zeile.validFrom
  const abgelaufen = !!gueltigBis && gueltigBis < heute
  const kuenftig = gueltigAb > heute

  const entfernen = async () => {
    if (!(await bestaetige(t('articles.customerPrices.removeConfirm'), { ok: t('articles.actions.remove'), gefaehrlich: true }))) return
    try {
      await articlesApi.deleteCustomerPrice(artikelId, zeile.id)
      await onGeaendert()
    } catch (err) {
      onFehler(fehlertext(err, t))
    }
  }

  return (
    <ZeilenBlock
      gruppe={gruppe}
      schluessel={zeile.id}
      gedimmt={abgelaufen}
      zusatz={
        <ZusatzFeld label={t('articles.customerPrices.note')} breite="w-96">
          <input maxLength={1000} className={zelle} {...notiz.props} />
        </ZusatzFeld>
      }
      zuInfo={
        (daten.note as string | null) ? <RasterInfo von="kunde" bis="einheit">{daten.note as string}</RasterInfo> : undefined
      }
    >
      <Spalte spalte="kunde">
        <span className="block truncate px-1.5 py-1 text-sm">
          {zeile.customer.clientNumber && <span className="mr-1 font-mono text-muted-foreground">{zeile.customer.clientNumber}</span>}
          <span className={zeile.customer.isActive ? '' : 'text-muted-foreground'}>{zeile.customer.name}</span>
        </span>
      </Spalte>
      <Spalte spalte="preis">
        <input
          inputMode="decimal"
          aria-label={t('articles.customerPrices.price')}
          className={`${zelle} text-right tabular-nums`}
          data-start
          {...preis.props}
        />
      </Spalte>
      <Spalte spalte="einheit">
        <span className="flex items-center gap-1">
          <PreiseinheitEingabe
            aria-label={t('articles.customerPrices.priceUnit')}
            className={`${zelle} text-right tabular-nums`}
            {...einheit.props}
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">{einheitKuerzel}</span>
        </span>
      </Spalte>
      <Spalte spalte="ab">
        <input type="date" aria-label={t('articles.customerPrices.validFrom')} className={`${zelle} tabular-nums`} {...ab.props} />
      </Spalte>
      <Spalte spalte="bis">
        <span className="flex items-center gap-1">
          <input type="date" aria-label={t('articles.customerPrices.validTo')} className={`${zelle} tabular-nums`} {...bis.props} />
          {abgelaufen && <span className="whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs">{t('articles.customerPrices.expired')}</span>}
          {kuenftig && (
            <span className="whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-xs text-foreground">
              {t('articles.customerPrices.upcoming')}
            </span>
          )}
        </span>
      </Spalte>
      <Spalte spalte="aktion">
        {canManage ? (
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title={t('articles.actions.remove')}
            aria-label={t('articles.actions.remove')}
            onClick={() => void entfernen()}
          >
            <Trash2 size={14} />
          </button>
        ) : (
          <span />
        )}
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald Kunde und Preis dastehen. */
function NeuerKundenpreis({
  artikelId,
  kunden,
  onAngelegt,
}: {
  artikelId: string
  kunden: CustomerRef[]
  onAngelegt: () => Promise<void>
}) {
  const { t } = useTranslation()
  const leer = { customerId: '', price: '', priceUnit: '1', validFrom: heuteLokal(), validTo: '', note: '' }
  const [neu, setNeu] = useState(leer)
  const [fehler, setFehler] = useState<string | null>(null)
  const setze = (teil: Partial<typeof leer>) => setNeu((n) => ({ ...n, ...teil }))

  const anlegen = async () => {
    if (!neu.customerId && !neu.price) return
    const preis = leseDezimal(neu.price)
    if (!neu.customerId) return setFehler(t('articles.customerPrices.choose'))
    if (preis === null || Number.isNaN(preis) || preis < 0) return setFehler(t('articles.priceInvalid'))
    if (zuVieleStellen(preis, 2)) return setFehler(t('articles.priceDecimals'))
    const einheit = lesePreiseinheit(neu.priceUnit)
    if (einheit === null) return setFehler(t('articles.priceUnitInvalid'))
    if (!neu.validFrom) return setFehler(t('articles.customerPriceDateInvalid'))
    if (neu.validTo && neu.validTo < neu.validFrom) return setFehler(t('articles.customerPriceValidRange'))
    try {
      await articlesApi.addCustomerPrice(artikelId, {
        customerId: neu.customerId,
        price: preis,
        priceUnit: einheit,
        note: neu.note.trim() || null,
        validFrom: neu.validFrom,
        // Leer = ohne Ablaufdatum.
        validTo: neu.validTo || null,
      })
      setNeu(leer)
      setFehler(null)
      await onAngelegt()
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  return (
    <ZeilenBlock
      neu
      onVerlassen={() => void anlegen()}
      onVerwerfen={() => {
        setNeu(leer)
        setFehler(null)
      }}
      fehler={fehler}
      zusatz={
        <ZusatzFeld label={t('articles.customerPrices.note')} breite="w-96">
          <input maxLength={1000} className={zelle} value={neu.note} onChange={(e) => setze({ note: e.target.value })} />
        </ZusatzFeld>
      }
    >
      <Spalte spalte="kunde">
        <select
          aria-label={t('articles.customerPrices.customer')}
          className={zelle}
          data-start
          value={neu.customerId}
          onChange={(e) => setze({ customerId: e.target.value })}
        >
          <option value="">{t('articles.customerPrices.choose')}</option>
          {/* Kein Ausfiltern: Ein Kunde kann mehrere Preise nacheinander haben (N-18). */}
          {kunden.map((k) => (
            <option key={k.id} value={k.id}>
              {k.clientNumber ? `${k.clientNumber} · ` : ''}
              {k.name}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="preis">
        <input
          inputMode="decimal"
          aria-label={t('articles.customerPrices.price')}
          className={`${zelle} text-right tabular-nums`}
          value={neu.price}
          onChange={(e) => setze({ price: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="einheit">
        <PreiseinheitEingabe
          aria-label={t('articles.customerPrices.priceUnit')}
          className={`${zelle} text-right tabular-nums`}
          value={neu.priceUnit}
          onChange={(e) => setze({ priceUnit: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="ab">
        <input
          type="date"
          aria-label={t('articles.customerPrices.validFrom')}
          className={`${zelle} tabular-nums`}
          value={neu.validFrom}
          onChange={(e) => setze({ validFrom: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="bis">
        <input
          type="date"
          aria-label={t('articles.customerPrices.validTo')}
          min={neu.validFrom || undefined}
          className={`${zelle} tabular-nums`}
          value={neu.validTo}
          onChange={(e) => setze({ validTo: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}

// ─── Einkauf ──────────────────────────────────────────────────────────────────

function EinkaufAbschnitt({
  artikel,
  zeilen,
  canManage,
  darfPartnerLesen,
  onGeaendert,
  onFehler,
}: {
  artikel: ArticleDetail
  zeilen: ArticleSupplierRow[]
  canManage: boolean
  darfPartnerLesen: boolean
  onGeaendert: () => Promise<void>
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  const [klappModus, setKlappModus] = useKlappModus('article_suppliers')
  const [partner, setPartner] = useState<SupplierRef[]>([])
  const [partnerFehler, setPartnerFehler] = useState(false)

  useEffect(() => {
    if (!canManage || !darfPartnerLesen) return
    articlesApi
      .suppliers()
      .then(({ data }) => setPartner(data.data))
      .catch(() => setPartnerFehler(true))
  }, [canManage, darfPartnerLesen])

  const spalten: SpaltenDef[] = [
    { key: 'stern', label: '', spur: '2rem', min: 28, schmal: 'w-10' },
    { key: 'lieferant', label: t('articles.supplier.supplier'), spur: 'minmax(9rem,2fr)', min: 150, schmal: 'w-full' },
    { key: 'artnr', label: t('articles.supplier.supplierArticleNumber'), spur: 'minmax(7rem,1fr)', min: 110, schmal: 'w-40' },
    { key: 'ek', label: t('articles.supplier.purchasePrice'), spur: '7rem', min: 85, schmal: 'w-28', rechts: true },
    { key: 'pe', label: t('articles.fields.priceUnit'), spur: '5rem', min: 65, schmal: 'w-24', rechts: true },
    { key: 'waehrung', label: t('articles.supplier.currency'), spur: '6.5rem', min: 85, schmal: 'w-28' },
    { key: 'lieferzeit', label: t('articles.supplier.leadTimeDays'), spur: '5rem', min: 65, schmal: 'w-24', rechts: true },
    { key: 'vpe', label: t('articles.supplier.packSize'), spur: '5rem', min: 65, schmal: 'w-24', rechts: true },
    { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
  ]

  const frei = partner.filter((p) => !zeilen.some((z) => z.supplierId === p.id))

  return (
    <Abschnittskarte titel={t('articles.sections.purchase')} aktion={<KlappUmschalter modus={klappModus} onModus={setKlappModus} texte={klappTexte(t)} />}>
      <ZeilenListe spalten={spalten} klappModus={klappModus}>
        {zeilen.length === 0 && (
          <p className="px-4 py-3 text-sm text-muted-foreground">{t('articles.supplier.none')}</p>
        )}
        {zeilen.map((z) => (
          <LieferantenZeile
            key={z.id}
            artikelId={artikel.id}
            einheitKuerzel={artikel.unit.code}
            zeile={z}
            canManage={canManage}
            onGeaendert={onGeaendert}
            onFehler={onFehler}
          />
        ))}
        {canManage &&
          (darfPartnerLesen && !partnerFehler ? (
            <NeueLieferantenZeile
              artikelId={artikel.id}
              partner={frei}
              erster={zeilen.length === 0}
              onAngelegt={onGeaendert}
            />
          ) : (
            <p className="px-4 py-3 text-sm text-destructive">{t('articles.supplier.loadFailed')}</p>
          ))}
      </ZeilenListe>
      <p className="mt-2 text-xs text-muted-foreground">{t('articles.hints.preferred')}</p>
      <p className="mt-1 text-xs text-muted-foreground">{t('articles.hints.tenantCurrency')}</p>
    </Abschnittskarte>
  )
}

function LieferantenZeile({
  artikelId,
  einheitKuerzel,
  zeile,
  canManage,
  onGeaendert,
  onFehler,
}: {
  artikelId: string
  einheitKuerzel: string
  zeile: ArticleSupplierRow
  canManage: boolean
  onGeaendert: () => Promise<void>
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  const gesperrt = !canManage
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `articleSupplier:${zeile.id}`,
    bezeichnung: zeile.supplier.name,
    daten: zeile as unknown as Datensatz,
    schreibe: (patch, stand) =>
      articlesApi.updateSupplier(artikelId, zeile.id, patch, stand).then((r) => r.data as unknown as Datensatz),
    keepalive: { url: `/articles/${artikelId}/suppliers/${zeile.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: zeile.supplier.name })
  const daten = useStellenDaten(stelle)

  const artNr = useFeld<string | null>(stelle, 'supplierArticleNumber', {
    gruppe,
    gesperrt,
    label: t('articles.supplier.supplierArticleNumber'),
  })
  const preis = useFeld<number | null>(stelle, 'purchasePrice', betragFeld(t, t('articles.supplier.purchasePrice'), gesperrt, gruppe))
  const einheit = useFeld<number | null>(stelle, 'priceUnit', preiseinheitFeld(t, gesperrt, gruppe))
  const waehrung = useFeld<string | null>(stelle, 'currency', {
    gruppe,
    gesperrt,
    art: 'auswahl',
    label: t('articles.supplier.currency'),
  })
  const lieferzeit = useFeld<number | null>(stelle, 'leadTimeDays', {
    gruppe,
    gesperrt,
    art: 'zahl',
    stellen: 0,
    label: t('articles.supplier.leadTimeDays'),
    pruefe: (w) =>
      w !== null && (w < 0 || w > 999 || !Number.isInteger(w))
        ? t('articles.leadTimeInvalid', { defaultValue: 'Die Lieferzeit ist eine ganze Zahl von 0 bis 999 Tagen.' })
        : null,
  })
  const vpe = useFeld<number | null>(stelle, 'packSize', {
    gruppe,
    gesperrt,
    art: 'zahl',
    label: t('articles.supplier.packSize'),
    pruefe: (w) => (w !== null && (w < 0 || zuVieleStellen(w, 3)) ? t('articles.packSizeInvalid') : null),
  })
  const notiz = useFeld<string | null>(stelle, 'note', { gruppe, gesperrt, label: t('articles.supplier.note') })

  // „Genau einer" (AP-28 4.2): Die Bevorzugung wird **gesetzt**, nie abgewählt —
  // eine Handlung, deren Meldung neben dem Stern steht. Sie verschiebt die
  // Bevorzugung der anderen Zeilen, deshalb danach neu laden.
  const bevorzugt = Boolean(daten.isPreferred)
  const stern = useAenderung(stelle, `articleSupplier:${zeile.id}:preferred`)

  const entfernen = async () => {
    if (!(await bestaetige(t('articles.actions.removeConfirm'), { ok: t('articles.actions.remove'), gefaehrlich: true }))) return
    try {
      await articlesApi.deleteSupplier(artikelId, zeile.id)
      await onGeaendert()
    } catch (err) {
      onFehler(fehlertext(err, t))
    }
  }

  return (
    <ZeilenBlock
      gruppe={gruppe}
      schluessel={zeile.id}
      zusatz={
        <ZusatzFeld label={t('articles.supplier.note')} breite="w-96">
          <input maxLength={1000} className={zelle} {...notiz.props} />
        </ZusatzFeld>
      }
      zuInfo={
        (daten.note as string | null) ? <RasterInfo von="lieferant" bis="ek">{daten.note as string}</RasterInfo> : undefined
      }
    >
      <Spalte spalte="stern" label="">
        <span className="flex items-center gap-1">
          <button
            type="button"
            disabled={!canManage || bevorzugt}
            title={bevorzugt ? t('articles.supplier.preferred') : t('articles.actions.setPreferred')}
            aria-label={bevorzugt ? t('articles.supplier.preferred') : t('articles.actions.setPreferred')}
            onClick={async () => {
              const ergebnis = await stern.sende({ felder: ['isPreferred'], patch: { isPreferred: true }, wert: 'isPreferred' })
              if (ergebnis.art === 'gespeichert') await onGeaendert()
            }}
          >
            <Star size={16} className={bevorzugt ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'} />
          </button>
          <FeldStatusZeichen status={stern.status} />
        </span>
      </Spalte>
      <Spalte spalte="lieferant">
        <span className="block truncate px-1.5 py-1 text-sm">
          {zeile.supplier.supplierNumber && <span className="mr-1 font-mono text-muted-foreground">{zeile.supplier.supplierNumber}</span>}
          {zeile.supplier.name}
        </span>
      </Spalte>
      <Spalte spalte="artnr">
        <input
          maxLength={60}
          aria-label={t('articles.supplier.supplierArticleNumber')}
          className={`${zelle} font-mono`}
          data-start
          {...artNr.props}
        />
      </Spalte>
      <Spalte spalte="ek">
        <input
          inputMode="decimal"
          aria-label={t('articles.supplier.purchasePrice')}
          className={`${zelle} text-right tabular-nums`}
          {...preis.props}
        />
      </Spalte>
      <Spalte spalte="pe">
        <span className="flex items-center gap-1">
          <PreiseinheitEingabe
            aria-label={t('articles.fields.priceUnit')}
            className={`${zelle} text-right tabular-nums`}
            {...einheit.props}
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">{einheitKuerzel}</span>
        </span>
      </Spalte>
      <Spalte spalte="waehrung">
        <select aria-label={t('articles.supplier.currency')} className={zelle} {...waehrung.props}>
          <option value="">—</option>
          {CURRENCIES.map((w) => (
            <option key={w.code} value={w.code}>
              {w.code} — {w.de}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="lieferzeit">
        <input
          inputMode="numeric"
          aria-label={t('articles.supplier.leadTimeDays')}
          className={`${zelle} text-right tabular-nums`}
          {...lieferzeit.props}
        />
      </Spalte>
      <Spalte spalte="vpe">
        <input
          inputMode="decimal"
          aria-label={t('articles.supplier.packSize')}
          className={`${zelle} text-right tabular-nums`}
          {...vpe.props}
        />
      </Spalte>
      <Spalte spalte="aktion">
        {canManage ? (
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title={t('articles.actions.remove')}
            aria-label={t('articles.actions.remove')}
            onClick={() => void entfernen()}
          >
            <Trash2 size={14} />
          </button>
        ) : (
          <span />
        )}
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald ein Lieferant gewählt ist. */
function NeueLieferantenZeile({
  artikelId,
  partner,
  erster,
  onAngelegt,
}: {
  artikelId: string
  partner: SupplierRef[]
  erster: boolean
  onAngelegt: () => Promise<void>
}) {
  const { t } = useTranslation()
  const leer = { supplierId: '', supplierArticleNumber: '', purchasePrice: '', priceUnit: '1', currency: '', leadTimeDays: '', packSize: '', note: '' }
  const [neu, setNeu] = useState(leer)
  const [fehler, setFehler] = useState<string | null>(null)
  const setze = (teil: Partial<typeof leer>) => setNeu((n) => ({ ...n, ...teil }))

  const anlegen = async () => {
    if (!neu.supplierId) return
    const preis = leseDezimal(neu.purchasePrice)
    if (preis !== null && (Number.isNaN(preis) || preis < 0)) return setFehler(t('articles.priceInvalid'))
    if (preis !== null && zuVieleStellen(preis, 2)) return setFehler(t('articles.priceDecimals'))
    const einheit = lesePreiseinheit(neu.priceUnit)
    if (einheit === null) return setFehler(t('articles.priceUnitInvalid'))
    const vpe = leseDezimal(neu.packSize)
    if (vpe !== null && (Number.isNaN(vpe) || vpe < 0 || zuVieleStellen(vpe, 3))) return setFehler(t('articles.packSizeInvalid'))
    try {
      await articlesApi.addSupplier(artikelId, {
        supplierId: neu.supplierId,
        supplierArticleNumber: neu.supplierArticleNumber || null,
        purchasePrice: preis,
        priceUnit: einheit,
        currency: neu.currency || null,
        leadTimeDays: neu.leadTimeDays.trim() === '' ? null : Number(neu.leadTimeDays),
        packSize: vpe,
        note: neu.note || null,
        // Die erste Zeile eines Artikels ist die bevorzugte (AP-28 4.2).
        ...(erster && { isPreferred: true }),
      })
      setNeu(leer)
      setFehler(null)
      await onAngelegt()
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }

  return (
    <ZeilenBlock
      neu
      onVerlassen={() => void anlegen()}
      onVerwerfen={() => {
        setNeu(leer)
        setFehler(null)
      }}
      fehler={fehler}
      zusatz={
        <ZusatzFeld label={t('articles.supplier.note')} breite="w-96">
          <input maxLength={1000} className={zelle} value={neu.note} onChange={(e) => setze({ note: e.target.value })} />
        </ZusatzFeld>
      }
    >
      <Spalte spalte="stern" leer>
        <span />
      </Spalte>
      <Spalte spalte="lieferant">
        <select
          aria-label={t('articles.supplier.supplier')}
          className={zelle}
          data-start
          value={neu.supplierId}
          onChange={(e) => setze({ supplierId: e.target.value })}
        >
          <option value="">{t('articles.supplier.choose')}</option>
          {partner.map((p) => (
            <option key={p.id} value={p.id}>
              {p.supplierNumber ? `${p.supplierNumber} · ` : ''}
              {p.name}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="artnr">
        <input
          maxLength={60}
          aria-label={t('articles.supplier.supplierArticleNumber')}
          className={`${zelle} font-mono`}
          value={neu.supplierArticleNumber}
          onChange={(e) => setze({ supplierArticleNumber: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="ek">
        <input
          inputMode="decimal"
          aria-label={t('articles.supplier.purchasePrice')}
          className={`${zelle} text-right tabular-nums`}
          value={neu.purchasePrice}
          onChange={(e) => setze({ purchasePrice: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="pe">
        <PreiseinheitEingabe
          aria-label={t('articles.fields.priceUnit')}
          className={`${zelle} text-right tabular-nums`}
          value={neu.priceUnit}
          onChange={(e) => setze({ priceUnit: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="waehrung">
        <select
          aria-label={t('articles.supplier.currency')}
          className={zelle}
          value={neu.currency}
          onChange={(e) => setze({ currency: e.target.value })}
        >
          <option value="">—</option>
          {CURRENCIES.map((w) => (
            <option key={w.code} value={w.code}>
              {w.code} — {w.de}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="lieferzeit">
        <input
          type="number"
          min={0}
          max={999}
          aria-label={t('articles.supplier.leadTimeDays')}
          className={`${zelle} text-right tabular-nums`}
          value={neu.leadTimeDays}
          onChange={(e) => setze({ leadTimeDays: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="vpe">
        <input
          inputMode="decimal"
          aria-label={t('articles.supplier.packSize')}
          className={`${zelle} text-right tabular-nums`}
          value={neu.packSize}
          onChange={(e) => setze({ packSize: e.target.value })}
        />
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}

// ─── Bild ─────────────────────────────────────────────────────────────────────

/** Ein Bild je Artikel — es beantwortet „ist das das richtige Teil?" (AP-28 4.4). */
function BildAbschnitt({
  artikel,
  canManage,
  onGeaendert,
  onHinweis,
  onFehler,
}: {
  artikel: ArticleDetail
  canManage: boolean
  onGeaendert: (a: ArticleDetail) => void
  onHinweis: (text: string | null) => void
  onFehler: (text: string) => void
}) {
  const { t } = useTranslation()
  const [laedt, setLaedt] = useState(false)

  const hochladen = async (datei: File | undefined) => {
    if (!datei) return
    setLaedt(true)
    onHinweis(null)
    try {
      const { data } = await articlesApi.uploadImage(artikel.id, datei)
      onGeaendert(data)
      // Verkleinert hat der Server (N-13) — sagen, was aus dem Bild geworden ist.
      if (data.imageCompression) {
        onHinweis(
          t('articles.imageCompressed', {
            before: formatMegabyte(data.imageCompression.originalBytes),
            after: formatMegabyte(data.imageCompression.storedBytes),
          }),
        )
      }
    } catch (err) {
      onFehler(fehlertext(err, t))
    } finally {
      setLaedt(false)
    }
  }

  const entfernen = async () => {
    try {
      onGeaendert((await articlesApi.deleteImage(artikel.id)).data)
    } catch (err) {
      onFehler(fehlertext(err, t))
    }
  }

  return (
    <Abschnittskarte
      titel={t('articles.sections.image')}
      aktion={
        canManage && artikel.hasImage ? (
          <button
            type="button"
            onClick={() => void entfernen()}
            className="rounded-md border border-input px-3 py-1 text-sm text-foreground hover:bg-muted/50"
          >
            {t('articles.actions.removeImage')}
          </button>
        ) : undefined
      }
    >
      <div className="flex flex-wrap items-center gap-4">
        {artikel.imageUrl ? (
          <img src={artikel.imageUrl} alt={artikel.name} className="h-24 w-24 rounded-md border border-border object-cover" />
        ) : (
          <div className="grid h-24 w-24 place-items-center rounded-md bg-muted text-xs text-muted-foreground">
            {artikel.hasImage ? '…' : '—'}
          </div>
        )}
        <div className="space-y-2">
          {canManage && (
            <label className="inline-flex cursor-pointer items-center rounded-md border border-input px-3 py-1.5 text-sm text-foreground hover:bg-muted/50">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                disabled={laedt}
                onChange={(e) => {
                  void hochladen(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              {laedt ? t('common.loading') : t('articles.actions.uploadImage')}
            </label>
          )}
          <p className="text-xs text-muted-foreground">{t('articles.hints.image')}</p>
        </div>
      </div>
    </Abschnittskarte>
  )
}

// ─── Fremdsystem ──────────────────────────────────────────────────────────────

function FremdsystemAbschnitt({ artikel }: { artikel: ArticleDetail }) {
  const { t } = useTranslation()
  return (
    <Abschnittskarte titel={t('articles.sections.external')} aktion={<span className="text-xs text-muted-foreground">{t('articles.hints.external')}</span>}>
      <div className="space-y-2">
        <FeldZeile label={t('articles.fields.externalId')}>
          <input disabled value={artikel.externalId ?? ''} className={feldKlasse} />
        </FeldZeile>
        <FeldZeile label={t('articles.fields.externalSource')}>
          <input disabled value={artikel.externalSource ?? ''} className={feldKlasse} />
        </FeldZeile>
      </div>
    </Abschnittskarte>
  )
}
